import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { ProgressBar, StatusBadge } from './ui';
import { byId, derivedAssignmentStatus, getAssignmentProgress, getCourse, getOwner, fullName, getProgramProgress, getProgramCourses, programGrade, trainerNames } from '../utils/lmsSelectors';

export function AdminCourseCard({ data, course, onDelete }) {
  const owner = getOwner(data, course);
  const assignments = data.assignments.filter((assignment) => assignment.courseId === course.id && assignment.status !== 'revoked');
  const completed = assignments.filter((assignment) => assignment.status === 'completed').length;
  const completionRate = assignments.length ? Math.round((completed / assignments.length) * 100) : 0;
  return (
    <article className="course-card">
      <div className="card-row between">
        <StatusBadge status={course.status} />
        <small>v{byId(data.courseVersions, course.currentVersionId)?.versionNumber || 1}</small>
      </div>
      <h3><Link className="text-link" to={`/admin/courses/${course.id}`}>{course.title}</Link></h3>
      <p>{course.description}</p>
      <div className="card-meta">
        <span>{course.category}</span>
        <span>{course.difficulty}</span>
        <span>{course.estimatedMinutes} min</span>
      </div>
      <p className="muted small">Owner: {fullName(owner)} • Trainers: {trainerNames(data, course)} • Assigned: {assignments.length}</p>
      <ProgressBar value={completionRate} />
      <div className="button-row compact course-card-actions">
        <Link to={`/admin/courses/${course.id}`} className="button button-secondary full">Manage course</Link>
        {onDelete && <button className="button button-danger" type="button" onClick={() => onDelete(course)}>Delete</button>}
      </div>
    </article>
  );
}

export function LearnerCourseCard({ data, assignment }) {
  const course = getCourse(data, assignment.courseId);
  const status = derivedAssignmentStatus(data, assignment);
  const progress = getAssignmentProgress(data, assignment);
  return (
    <article className="course-card">
      <div className="card-row between">
        <StatusBadge status={status} />
        {assignment.required && <span className="pill">Required</span>}
      </div>
      <h3><Link className="text-link" to={`/learner/course/${assignment.id}`}>{course?.title}</Link></h3>
      <p>{course?.description}</p>
      <div className="card-meta">
        <span>{course?.category}</span>
        <span>{course?.difficulty}</span>
        <span>{course?.estimatedMinutes} min</span>
      </div>
      <p className="muted small">Trainers: {trainerNames(data, course)}</p>
      <p className="muted small"><CalendarDays size={14} /> Due: {assignment.dueDate || 'No due date'}</p>
      <ProgressBar value={progress.percent} />
      <Link to={`/learner/course/${assignment.id}`} className="button button-primary full">
        {assignment.status === 'completed' ? 'View summary' : 'Continue training'}
      </Link>
    </article>
  );
}


export function LearnerProgramCard({ data, programAssignment }) {
  const program = byId(data.programs || [], programAssignment.programId);
  const progress = getProgramProgress(data, programAssignment);
  const courses = getProgramCourses(data, programAssignment.programId);
  const grade = programGrade(data, programAssignment);
  return (
    <article className="course-card program-card">
      <div className="card-row between">
        <StatusBadge status={progress.status} />
        {programAssignment.required && <span className="pill">Required</span>}
      </div>
      <h3><Link className="text-link" to={`/learner/program/${programAssignment.id}`}>{program?.title}</Link></h3>
      <p>{program?.description}</p>
      <div className="card-meta">
        <span>{program?.category}</span>
        <span>{courses.length} courses</span>
        <span>{grade === null ? 'No grade yet' : `${grade}% grade`}</span>
      </div>
      <p className="muted small"><CalendarDays size={14} /> Due: {programAssignment.dueDate || 'No due date'}</p>
      <ProgressBar value={progress.percent} />
      <Link to={`/learner/program/${programAssignment.id}`} className="button button-primary full">
        {progress.status === 'completed' ? 'View program summary' : 'Continue program'}
      </Link>
    </article>
  );
}
